/** Create/edit dialog for a system-wide event group. Event codes are selected
 *  from an autocomplete restricted to the canonical code registry. */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Stack, TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import { eventGroupsApi, type EventGroupWriteInput } from '@/api/eventGroups';
import { useEditorHistoryTabs } from '@/components/History/useEditorHistoryTabs';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';

const Schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(128),
  description: z.string().max(512).optional().default(''),
  event_codes: z.array(z.string()).default([]),
});
type Input = z.infer<typeof Schema>;

interface Props {
  groupId: number | null;
  onClose: () => void;
  onSaved: () => void;
}

export function EventGroupDialog({ groupId, onClose, onSaved }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const isEdit = groupId !== null;
  const history = useEditorHistoryTabs({
    enabled: isEdit && hasPerm('console_audit_view'),
    contentType: 'demo.notification_event_group',
    objectPk: groupId,
    onRestored: () => void qc.invalidateQueries({ queryKey: ['consoleEventGroups'] }),
  });

  const groupQuery = useQuery({
    queryKey: ['consoleEventGroup', groupId],
    queryFn: () => eventGroupsApi.retrieve(groupId as number),
    enabled: isEdit,
  });
  const codesQuery = useQuery({
    queryKey: ['consoleEventGroupCodes'],
    queryFn: () => eventGroupsApi.eventCodes(),
  });

  const { control, register, handleSubmit, reset, formState: { errors, isSubmitting } } =
    useForm<Input>({
      resolver: zodResolver(Schema),
      defaultValues: { name: '', description: '', event_codes: [] },
    });

  useEffect(() => {
    if (!isEdit || !groupQuery.data) return;
    reset({
      name: groupQuery.data.name,
      description: groupQuery.data.description ?? '',
      event_codes: groupQuery.data.event_codes,
    });
  }, [isEdit, groupQuery.data, reset]);

  const mutation = useMutation({
    mutationFn: (values: Input) => {
      const body: EventGroupWriteInput = {
        name: values.name.trim(),
        description: values.description.trim() || null,
        event_codes: values.event_codes,
      };
      return isEdit ? eventGroupsApi.update(groupId as number, body) : eventGroupsApi.create(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consoleEventGroups'] });
      snackbar.show(isEdit ? 'Event group updated' : 'Event group created', 'success');
      onSaved();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed', 'error'),
  });

  const onSubmit = handleSubmit((values) => mutation.mutate(values));
  const loading = isEdit && groupQuery.isLoading;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit event group' : 'New event group'}</DialogTitle>
      <DialogContent dividers>
        {history.header}
        {history.historyPane ??
        (loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <Box component="form" onSubmit={onSubmit} noValidate>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField label="Name" fullWidth error={Boolean(errors.name)}
                helperText={errors.name?.message} {...register('name')} />
              <TextField label="Description" fullWidth multiline {...register('description')} />
              <Controller control={control} name="event_codes" render={({ field }) => (
                <Autocomplete multiple options={codesQuery.data ?? []} value={field.value}
                  onChange={(_, v) => field.onChange(v)}
                  renderInput={(params) => (
                    <TextField {...params} label="Event codes"
                      placeholder="Add event code"
                      helperText="Only codes from the registry can be added" />
                  )} />
              )} />
            </Stack>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        {history.showingHistory ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={isSubmitting || mutation.isPending}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
